import React, { useState, useMemo, useEffect } from 'react';
import { Platform, StyleSheet, useWindowDimensions } from 'react-native';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import ModelSelectorDetailed from '@/components/Navigation/ModelSelectorDetailed';
import { useTheme } from '@/context/ThemeContext';

const createStyles = (colors, windowHeight, pwaSafeAreaTop) => StyleSheet.create({
  dropdownContent: {
    // Sized to match the desktop V6 model selector layout (160px left rail
    // + 2-up MiniCard grid). Keep this in sync with
    // ModelSelectorDetailedModal.modalOptions so the popover and the modal
    // entry points feel like the same surface.
    width: 660,
    maxHeight: Math.min(580, windowHeight - 140),
    height: Math.min(580, windowHeight - 140),
    backgroundColor: colors.solid_color_primary,
    borderRadius: 16,
    padding: 0,
    paddingTop: pwaSafeAreaTop ? pwaSafeAreaTop + 8 : 0,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
});

function ModelSelectorDetailedDropdown({
  TriggerComponent,
  triggerComponentProps = {},
  noRoot,
  modelIds,
  onChange,
  maxModels = 5,
  inputs = [],
  outputs = [],
  tags = [],
  shouldCloseOnSelect = true,
  isDisabled,
  onOpenChange,
  noAutoMode = false,
  noMultiSelectMode = false,
  showDoneButton,
  showSelectedModels,
  restrictMultipleModelsToSameType,
  dropdownSide = 'bottom', // 'bottom' opens below trigger, 'top' opens above
}) {
  const [open, setOpen] = useState(false);
  const { colors } = useTheme();
  const { height: windowHeight } = useWindowDimensions();
  const [pwaSafeAreaTop, setPwaSafeAreaTop] = useState(0);
  const pwaTopOffset = pwaSafeAreaTop + 24;
  const styles = useMemo(
    () => createStyles(colors, windowHeight, pwaSafeAreaTop),
    [colors, windowHeight, pwaSafeAreaTop]
  );

  useEffect(() => {
    if (Platform.OS !== 'web') return;

    const readSafeArea = () => {
      const raw = getComputedStyle(document.documentElement).getPropertyValue('--pwa-safe-area-top') || '0';
      const parsed = parseInt(raw, 10);
      setPwaSafeAreaTop(Number.isNaN(parsed) ? 0 : parsed);
    };

    readSafeArea();
    window.addEventListener('resize', readSafeArea);
    return () => window.removeEventListener('resize', readSafeArea);
  }, []);

  const content = (
    <>
      {TriggerComponent && (
        <DropdownMenu.Trigger disabled={isDisabled}>
          <TriggerComponent {...triggerComponentProps} />
        </DropdownMenu.Trigger>
      )}

      <DropdownMenu.Content
        style={styles.dropdownContent}
        className={Platform.OS === 'web' ? 'DropdownMenuContent ModelSelectorDropdown' : undefined}
        align="end"
        side="top"
        sideOffset={8 + pwaTopOffset}
        avoidCollisions={true}
        collisionPadding={{ top: 60 + pwaTopOffset, bottom: 80, left: 16, right: 16 }}
      >
        <ModelSelectorDetailed
          modelIds={modelIds}
          onChange={onChange}
          maxModels={maxModels}
          inputs={inputs}
          outputs={outputs}
          tags={tags}
          shouldCloseOnSelect={shouldCloseOnSelect}
          isDisabled={isDisabled}
          onRequestClose={() => setOpen(false)}
          noAutoMode={noAutoMode}
          noMultiSelectMode={noMultiSelectMode}
          showDoneButton={showDoneButton}
          showSelectedModels={showSelectedModels}
          restrictMultipleModelsToSameType={restrictMultipleModelsToSameType}
        />
      </DropdownMenu.Content>
    </>
  );

  if (noRoot) {
    return content;
  }

  return (
    <DropdownMenu.Root open={open} onOpenChange={(next) => { setOpen(next); onOpenChange && onOpenChange(next); }}>
      {content}
    </DropdownMenu.Root>
  );
}

export default React.memo(ModelSelectorDetailedDropdown);
